import { useId, useRef, useState } from "react";
import { cn } from "../../utils/cn";
import { Field } from "./Field";
import type { SelectOption } from "./Select";
import { useClickOutside } from "../../hooks/useClickOutside";

export interface MultiSelectProps {
  label?: string;
  helper?: string;
  error?: string;
  placeholder?: string;
  values: string[];
  onChange: (values: string[]) => void;
  options: SelectOption[];
  disabled?: boolean;
  required?: boolean;
  searchable?: boolean;
}

export function MultiSelect({
  label,
  helper,
  error,
  placeholder = "Select…",
  values,
  onChange,
  options,
  disabled,
  required,
  searchable = true,
}: MultiSelectProps) {
  const id = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [dropUp, setDropUp] = useState(false);
  const [query, setQuery] = useState("");

  useClickOutside(rootRef, () => setOpen(false), open);

  const checkPosition = () => {
    if (rootRef.current) {
      const rect = rootRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      setDropUp(spaceBelow < 220 && spaceAbove > spaceBelow);
    }
  };

  const selectedOptions = options.filter((o) => values.includes(o.value));
  const filtered = query
    ? options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))
    : options;

  const toggle = (value: string) => {
    onChange(values.includes(value) ? values.filter((v) => v !== value) : [...values, value]);
  };

  return (
    <Field label={label} htmlFor={id} helper={helper} error={error} required={required}>
      <div ref={rootRef} className="relative">
        <button
          type="button"
          id={id}
          disabled={disabled}
          onClick={() => {
            if (!open) checkPosition();
            setOpen((v) => !v);
            setQuery("");
          }}
          className={cn(
            "flex min-h-9 w-full cursor-pointer flex-wrap items-center gap-1 rounded-md border bg-background-50 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400/40 disabled:cursor-not-allowed disabled:bg-background-100",
            error ? "border-danger-300" : "border-background-300",
            open && "ring-2 ring-primary-400/40"
          )}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          {selectedOptions.length === 0 ? (
            <span className="px-1 text-foreground-400">{placeholder}</span>
          ) : (
            selectedOptions.map((opt) => (
              <span
                key={opt.value}
                className="inline-flex items-center gap-1 rounded bg-secondary-100 px-2 py-0.5 text-xs font-medium text-secondary-900"
              >
                {opt.label}
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggle(opt.value);
                  }}
                  className="flex h-3.5 w-3.5 cursor-pointer items-center justify-center rounded-full hover:bg-secondary-200"
                >
                  <i className="ri-close-line text-xs" />
                </span>
              </span>
            ))
          )}
          <i className="ri-arrow-down-s-line ml-auto text-base text-foreground-400" />
        </button>

        {open ? (
          <div
            className={cn(
              "absolute z-50 min-w-full w-max max-w-sm rounded-md border border-background-200 bg-background-50 shadow-xl shadow-foreground-950/10",
              dropUp ? "bottom-full mb-1" : "top-full mt-1"
            )}
          >
            {searchable ? (
              <div className="border-b border-background-100 p-1.5">
                <input
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Type to search…"
                  className="h-8 w-full rounded-md border border-background-200 bg-background-50 px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400/40"
                />
              </div>
            ) : null}
            <div className="max-h-60 overflow-y-auto py-1 rounded-md" role="listbox">
              {filtered.length === 0 ? (
                <div className="px-3 py-2 text-xs text-foreground-500">No options found</div>
              ) : (
                filtered.map((option) => {
                  const checked = values.includes(option.value);
                  return (
                    <button
                      type="button"
                      key={option.value}
                      role="option"
                      aria-selected={checked}
                      onClick={() => toggle(option.value)}
                      className="flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-sm hover:bg-background-100"
                    >
                      <input
                        type="checkbox"
                        readOnly
                        checked={checked}
                        className="h-4 w-4 cursor-pointer accent-primary-600"
                      />
                      <span className="truncate">{option.label}</span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        ) : null}
      </div>
    </Field>
  );
}